import { Server, Unplug } from 'lucide-react';
import { useState } from 'react';
import { ConnectionForm } from '@/components/connection-form';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui';
import { useConnection } from '@/hooks/use-connection';
import type { Connection } from '@/lib/connection';

/** Strips the scheme, so the bar shows `127.0.0.1:3100` rather than a wrapped URL. */
function shortLabel(apiUrl: string): string {
  return apiUrl.replace(/^https?:\/\//, '');
}

/**
 * Which service the console is pointed at, and the way to change it.
 *
 * In the header rather than in preferences: once the console can point anywhere, "which
 * service am I looking at" is a question every chain and backtest on the page depends on,
 * and getting it wrong is silent.
 */
export function ConnectionControl() {
  const { connection, connect, disconnect } = useConnection();
  const [open, setOpen] = useState(false);

  if (connection === undefined) {
    return null;
  }

  const onConnect = (next: Connection, remember: boolean): void => {
    connect(next, remember);
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        title={`Connected to ${connection.apiUrl} — click to change`}
        className="max-w-[14rem] font-mono text-[11px] text-muted"
      >
        <Server className="size-3.5 shrink-0" />
        <span className="hidden truncate sm:inline">{shortLabel(connection.apiUrl)}</span>
        <span className="sr-only sm:hidden">Switch service</span>
      </Button>

      <Modal open={open} title="Switch service" onClose={() => setOpen(false)} className="w-[min(420px,calc(100vw-32px))]">
        {/* The modal mounts this only while open, so the form seeds from the live connection
            on each opening instead of being synced by an effect that would clobber typing. */}
        <div className="flex flex-col gap-3 overflow-y-auto px-3 py-3">
          <p className="text-xs text-ink-2">Point this console at a different Fleece service. Everything loaded from the current one is discarded.</p>
          <ConnectionForm initial={connection} onConnect={onConnect} submitLabel="Switch" />
        </div>
        <footer className="flex shrink-0 justify-start border-t border-line px-3 py-2">
          <Button
            variant="ghost"
            onClick={() => {
              disconnect();
              setOpen(false);
            }}
          >
            <Unplug className="size-3.5" />
            Disconnect and forget this service
          </Button>
        </footer>
      </Modal>
    </>
  );
}
